import React, { useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { useUpdateEffect } from '@tager/web-core';

import Lock from '@/assets/svg/lock.svg';
import Arrow from '@/assets/svg/arrow-down.svg';
import CheckIcon from '@/assets/svg/checkbox-tick.svg';
import { colors } from '@/constants/theme';
import { ScrollStyles, TextLimit } from '@/components/styles';
import { OptionType } from '@/typings/common';
import { useOnClickOutside } from '@/hooks/useOnClickOutside';
import CrossIcon from '@/assets/svg/close-12.svg';
import Link from '@/components/Link';

import { StockFilterMultiSelectProps } from './types';

function StockFilterMultiSelect({
  label,
  options,
  selectedOptions,
  widgetsOptions,
  disabled,
  onChange,
  onClear,
}: StockFilterMultiSelectProps) {
  const [resultOptions, setResultOptions] = useState(options);
  const [search, setSearch] = useState('');
  const [isOpen, setOpened] = useState(false);

  const componentRef = useRef<HTMLDivElement | null>(null);

  useOnClickOutside(componentRef, () => {
    if (isOpen) {
      setOpened(false);
      handleResetOptionSearch();
    }
  });

  useUpdateEffect(() => {
    setResultOptions(options);
  }, [options]);

  const handleOpenSelect = () => {
    setOpened(!isOpen);
    handleResetOptionSearch();
  };

  const handleResetOptionSearch = () => {
    if (widgetsOptions?.withSearch && search) {
      setSearch('');
      setResultOptions(options);
    }
  };

  const handleOptionSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    setSearch(value);

    if (value) {
      setResultOptions(
        resultOptions.filter((option) =>
          option.label.toLowerCase().includes(value.toLowerCase())
        )
      );
    } else {
      setResultOptions(options);
    }
  };

  const handleOptionClick = (option: OptionType) => {
    onChange?.(option);
    handleResetOptionSearch();
  };

  const handleClear = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.stopPropagation();
    onClear?.();
    handleResetOptionSearch();
  };

  return (
    <Component>
      {label && <Label>{label}</Label>}

      <SelectWrapper ref={componentRef}>
        <Select onClick={handleOpenSelect} disabled={disabled}>
          <Picked>
            {selectedOptions &&
              selectedOptions.length > 0 &&
              selectedOptions.map((item) => item.label).join(', ')}
          </Picked>

          <Widgets>
            {!widgetsOptions?.withoutSelected &&
              selectedOptions &&
              selectedOptions.length > 0 && (
                <PickedLabel>Выбрано {selectedOptions.length}</PickedLabel>
              )}
            {!widgetsOptions?.withoutSelected &&
              onClear &&
              selectedOptions &&
              selectedOptions.length > 0 && (
                <ClearButton onClick={handleClear}>
                  <CrossIcon />
                </ClearButton>
              )}
            <IconWrapper isOpen={isOpen} disabled={disabled}>
              {disabled ? <Lock /> : <Arrow />}
            </IconWrapper>
          </Widgets>
        </Select>
        <Content isOpen={isOpen}>
          {widgetsOptions?.withSearch && (
            <SearchInput
              value={search}
              onChange={handleOptionSearch}
              placeholder="Поиск"
            />
          )}

          {resultOptions && resultOptions.length > 0 ? (
            resultOptions.map((option, index) => {
              const isActive = selectedOptions?.find(
                (selectedOption) => selectedOption.value === option.value
              );

              const optionInner = (
                <React.Fragment>
                  {option.label}
                  {option.icon && (
                    <BodyImageWrapper isActive={!!isActive}>
                      {option.icon}
                    </BodyImageWrapper>
                  )}
                  {isActive && (
                    <SvgWrapper>
                      <CheckIcon />
                    </SvgWrapper>
                  )}
                </React.Fragment>
              );

              return !option.link ? (
                <Option
                  key={index}
                  isActive={!!isActive}
                  onClick={() =>
                    handleOptionClick({
                      value: option.value,
                      label: option.label,
                    })
                  }
                >
                  {optionInner}
                </Option>
              ) : (
                <OptionLink
                  key={index}
                  to={option.link}
                  isActive={!!isActive}
                  onClick={(
                    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
                  ) => {
                    event.preventDefault();
                    handleOptionClick({
                      value: option.value,
                      label: option.label,
                    });
                  }}
                >
                  {optionInner}
                </OptionLink>
              );
            })
          ) : (
            <NoOptions>
              {options && options.length > 0 ? 'Не найдено' : 'Нет опций'}
            </NoOptions>
          )}
        </Content>
      </SelectWrapper>
    </Component>
  );
}

export default StockFilterMultiSelect;

const Component = styled.div`
  position: relative;
  width: 100%;
`;

const Label = styled.p`
  font-size: 16px;
  line-height: 19px;
  margin-bottom: 10px;
  color: ${(props) => props.theme.blue100};
`;

const SelectWrapper = styled.div`
  position: relative;
  width: 100%;
  background: ${(props) => props.theme.white_1};
  border: 1px solid ${(props) => props.theme.gray1000};
  border-radius: 6px;
`;

const Select = styled.button`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 11px 15px 10px;
  height: 38px;

  &:disabled {
    cursor: not-allowed;
  }
`;

const Picked = styled.div`
  flex: 1;
  font-size: 16px;
  line-height: 19px;
  text-align: left;
  color: ${(props) => props.theme.black200};

  ${TextLimit};
`;

const Widgets = styled.div`
  display: flex;
  align-items: center;
  margin-left: 20px;
`;

const ClearButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-right: 10px;
  transition: 0.3s;

  &:hover {
    opacity: 0.8;
  }

  svg {
    width: 9px;
    height: 9px;

    path {
      fill: ${(props) => props.theme.main};
    }
  }
`;

const PickedLabel = styled.span`
  font-weight: 500;
  font-size: 14px;
  line-height: 19px;
  margin-right: 10px;
  color: ${(props) => props.theme.gray700};
`;

const IconWrapper = styled.div<{ isOpen: boolean; disabled?: boolean }>`
  svg {
    transition: 0.3s;
    transform: rotate(0deg);

    path {
      fill: ${(props) => props.theme.black200};
    }
  }

  ${({ isOpen }) =>
    isOpen &&
    css`
      svg {
        transform: rotate(180deg);

        path {
          fill: ${(props) => props.theme.main};
        }
      }
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      svg {
        path {
          fill: ${(props) => props.theme.main};
        }
      }
    `}
`;

const Content = styled.div<{ isOpen: boolean }>`
  z-index: 5;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  position: absolute;
  top: 40px;
  width: 100%;
  max-height: 0;
  overflow-y: auto;
  transition: all 0.3s ease-in-out;
  background: ${(props) => props.theme.white_1};
  opacity: 0;
  border: 1px solid ${(props) => props.theme.gray1000};
  box-shadow: ${(props) => props.theme.blackShadow100};
  ${ScrollStyles};

  ${({ isOpen }) =>
    isOpen &&
    css`
      pointer-events: auto;
      opacity: 1;
      max-height: 280px;
      transition: all 0.3s ease-in-out;
    `}
`;

const SearchInput = styled.input`
  z-index: 1;
  position: sticky;
  top: 0;
  height: 39px;
  padding: 10px 15px;
  font-size: 16px;
  line-height: 19px;
  border: none;
  border-bottom: 1px solid ${(props) => props.theme.gray1000};
  background: ${(props) => props.theme.white_1};
  color: ${(props) => props.theme.blue100};

  &::placeholder {
    color: ${(props) => props.theme.gray1500};
    opacity: 1;
  }
`;

const OptionStyles = css<{ isActive?: boolean }>`
  position: relative;
  cursor: pointer;
  height: 39px;
  padding: 10px 15px;
  font-size: 16px;
  line-height: 19px;
  transition: all 0.3s ease-in-out;
  color: ${(props) => props.theme.blue100};

  &:hover {
    background: ${(props) => props.theme.gray300};
  }

  ${({ isActive }) =>
    isActive &&
    css`
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-weight: 700;
      color: ${(props) => props.theme.black200};
    `}
`;

const Option = styled.div<{ isActive?: boolean }>`
  ${OptionStyles}
`;

const OptionLink = styled(Link)`
  ${OptionStyles}
`;

const SvgWrapper = styled.div`
  svg {
    flex: 0 0 23px;
    height: 19px;

    path {
      fill: ${colors.main};
      stroke: ${colors.main};
    }
  }
`;

const BodyImageWrapper = styled.div<{ isActive: boolean }>`
  position: absolute;
  top: 50%;
  right: 50px;
  transform: translateY(-50%);
  width: 39px;
  height: 39px;

  svg {
    width: 39px;
    height: 39px;

    path {
      fill: ${colors.gray1000};
    }
    circle {
      stroke: ${colors.gray1000};
    }
  }

  ${({ isActive }) =>
    isActive &&
    css`
      svg {
        path {
          fill: ${colors.main};
        }
        circle {
          stroke: ${colors.main};
        }
      }
    `}
`;

const NoOptions = styled.p`
  padding: 10px 15px;
  font-size: 16px;
  line-height: 19px;
  color: ${(props) => props.theme.blue100};
`;
